/**
 * Customer Intelligence Component
 * Shows customer insights, preferences, and order history
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Avatar,
  Tooltip,
  CircularProgress,
  Collapse,
  LinearProgress,
  Divider,
  Badge
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import StarIcon from '@mui/icons-material/Star';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import FavoriteIcon from '@mui/icons-material/Favorite';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { getCustomerIntelligenceAPI } from '../../API/posAiAPI';
import './CustomerIntelligence.css';

const tierColors = {
  'VIP': { bg: '#fef3c7', text: '#92400e', icon: '👑' },
  'Gold': { bg: '#fef9c3', text: '#854d0e', icon: '🥇' },
  'Silver': { bg: '#f1f5f9', text: '#475569', icon: '🥈' },
  'Regular': { bg: '#ecfdf5', text: '#065f46', icon: '✓' },
  'New': { bg: '#eff6ff', text: '#1e40af', icon: '🆕' }
};

const CustomerIntelligence = ({ 
  customerId, 
  customerName,
  onAddToCart,
  onSuggestProduct
}) => {
  const [intelligence, setIntelligence] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(true);
  
  // Draggable position state
  const [position, setPosition] = useState({ x: 16, y: 80 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const containerStartPos = useRef({ x: 0, y: 0 });

  const fetchIntelligence = useCallback(async () => {
    if (!customerId) {
      setIntelligence(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getCustomerIntelligenceAPI({ customerId });
      
      // Handle offline response gracefully
      if (response.offline) {
        console.log('📴 Customer intelligence offline - showing basic info only');
        setError('Customer insights unavailable offline');
        // Keep any existing intelligence data
        return;
      }
      
      if (response.success) {
        setIntelligence(response.data);
      } else {
        setError(response.message || 'Failed to load customer data');
      }
    } catch (err) {
      console.error('Failed to fetch customer intelligence:', err);
      setError('Could not load customer insights');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    fetchIntelligence();
  }, [fetchIntelligence]);

  const handleAddSuggested = (productName, productId = null) => {
    if (onSuggestProduct) {
      onSuggestProduct(productName, productId);
    }
  };

  // Drag handlers
  const handleDragStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
    dragStartPos.current = { x: clientX, y: clientY };
    containerStartPos.current = { ...position };
  };

  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;
    e.preventDefault();
    const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
    
    const deltaX = clientX - dragStartPos.current.x;
    const deltaY = clientY - dragStartPos.current.y;
    
    // Calculate new position (from left and top)
    const newX = Math.max(0, Math.min(window.innerWidth - 320, containerStartPos.current.x + deltaX));
    const newY = Math.max(0, Math.min(window.innerHeight - 100, containerStartPos.current.y + deltaY));
    
    setPosition({ x: newX, y: newY });
  }, [isDragging]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add/remove event listeners for drag
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove, { passive: false });
      window.addEventListener('touchend', handleDragEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchmove', handleDragMove);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  if (!customerId) {
    return null;
  }

  const tier = intelligence?.stats?.customerTier || 'New';
  const tierStyle = tierColors[tier] || tierColors['New'];

  return (
    <Box 
      className={`customer-intelligence-container ${isDragging ? 'dragging' : ''}`}
      style={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        zIndex: 999,
        cursor: isDragging ? 'grabbing' : 'default'
      }}
    >
      {/* Drag Handle */}
      <Box 
        className="ci-drag-handle"
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        style={{ background: tierStyle.bg }}
      >
        <DragIndicatorIcon fontSize="small" />
      </Box>
      
      {/* Header */}
      <Box 
        className="ci-header" 
        onClick={() => setExpanded(!expanded)}
        style={{ background: tierStyle.bg }}
      >
        <Box className="header-left">
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
              <span className="tier-badge">{tierStyle.icon}</span>
            }
          >
            <Avatar className="customer-avatar" style={{ background: tierStyle.text }}>
              {customerName?.charAt(0)?.toUpperCase() || <PersonIcon />}
            </Avatar>
          </Badge>
          <Box className="customer-info">
            <Typography variant="subtitle2" className="customer-name">
              {intelligence?.customer?.name || customerName || 'Customer'}
            </Typography>
            <Chip 
              label={tier} 
              size="small" 
              className="tier-chip"
              style={{ background: tierStyle.text, color: 'white' }}
            />
          </Box>
        </Box>
        <Box className="header-right">
          <Tooltip title="Refresh">
            <IconButton 
              size="small" 
              onClick={(e) => { e.stopPropagation(); fetchIntelligence(); }}
              disabled={loading}
            >
              <RefreshIcon fontSize="small" className={loading ? 'spinning' : ''} />
            </IconButton>
          </Tooltip>
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Collapse in={expanded}>
        <Box className="ci-content">
          {loading && (
            <Box className="loading-container">
              <CircularProgress size={20} />
              <Typography variant="caption">Loading insights...</Typography>
            </Box>
          )}

          {error && !loading && (
            <Typography variant="caption" color="error" className="error-text">
              {error}
            </Typography>
          )}

          {intelligence && !loading && (
            <>
              {/* Stats Row */}
              <Box className="stats-row">
                <Box className="stat-item">
                  <ShoppingBagIcon className="stat-icon" />
                  <Box>
                    <Typography variant="h6" className="stat-value">
                      {intelligence.stats?.totalOrders || 0}
                    </Typography>
                    <Typography variant="caption" className="stat-label">
                      Orders
                    </Typography>
                  </Box>
                </Box>
                
                <Divider orientation="vertical" flexItem />
                
                <Box className="stat-item">
                  <TrendingUpIcon className="stat-icon" style={{ color: '#059669' }} />
                  <Box>
                    <Typography variant="h6" className="stat-value">
                      ₹{intelligence.stats?.totalSpent || 0}
                    </Typography>
                    <Typography variant="caption" className="stat-label">
                      Total Spent
                    </Typography>
                  </Box>
                </Box>
                
                <Divider orientation="vertical" flexItem />
                
                <Box className="stat-item">
                  <AccessTimeIcon className="stat-icon" style={{ color: '#f59e0b' }} />
                  <Box>
                    <Typography variant="h6" className="stat-value">
                      {intelligence.stats?.daysSinceLastOrder ?? '-'}
                    </Typography>
                    <Typography variant="caption" className="stat-label">
                      Days Ago
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Loyalty Points */}
              {intelligence.customer?.points > 0 && (
                <Box className="points-box">
                  <StarIcon className="points-icon" />
                  <Typography variant="body2">
                    <strong>{intelligence.customer.points}</strong> loyalty points
                  </Typography>
                </Box>
              )}

              {/* Insights */}
              {intelligence.insights?.length > 0 && (
                <Box className="insights-section">
                  <Typography variant="caption" className="section-title">
                    Insights
                  </Typography>
                  <Box className="insights-list">
                    {intelligence.insights.map((insight, index) => (
                      <Box 
                        key={index} 
                        className={`insight-item ${insight.type}`}
                      >
                        {insight.type === 'warning' && <WarningIcon fontSize="small" />}
                        {insight.type === 'positive' && <CheckCircleIcon fontSize="small" />}
                        {insight.type === 'suggestion' && <InfoIcon fontSize="small" />}
                        <Typography variant="caption">
                          {insight.message}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Favorite Products */}
              {intelligence.preferences?.favoriteProducts?.length > 0 && (
                <Box className="favorites-section">
                  <Typography variant="caption" className="section-title">
                    <FavoriteIcon fontSize="small" /> Favorites
                  </Typography>
                  <Box className="favorites-list">
                    {intelligence.preferences.favoriteProducts.slice(0, 4).map((product, index) => (
                      <Chip
                        key={index}
                        label={`${product.name} (${product.orderCount}x)`}
                        size="small"
                        variant="outlined"
                        onClick={() => handleAddSuggested(product.name, product.productId)}
                        icon={<AddShoppingCartIcon fontSize="small" />}
                        className="favorite-chip"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Order Type Preference */}
              {intelligence.preferences?.orderTypeBreakdown && (
                <Box className="order-type-section">
                  <Typography variant="caption" className="section-title">
                    Order Preference
                  </Typography>
                  <Box className="order-type-bars">
                    {Object.entries(intelligence.preferences.orderTypeBreakdown).map(([type, count]) => {
                      const total = Object.values(intelligence.preferences.orderTypeBreakdown).reduce((a, b) => a + b, 0);
                      const percentage = total > 0 ? (count / total) * 100 : 0;
                      
                      return (
                        <Box key={type} className="order-type-bar">
                          <Box className="bar-label">
                            <Typography variant="caption">{type}</Typography>
                            <Typography variant="caption">{count}</Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={percentage}
                            className={`bar-progress ${type.toLowerCase()}`}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Quick Suggestions */}
              {intelligence.suggestedProducts?.length > 0 && (
                <Box className="suggestions-section">
                  <Typography variant="caption" className="section-title">
                    Quick Add (Their Usual)
                  </Typography>
                  <Box className="suggestions-buttons">
                    {intelligence.suggestedProducts.map((product, index) => {
                      // Handle both old format (string) and new format (object)
                      const name = typeof product === 'string' ? product : product.name;
                      const productId = typeof product === 'object' ? product.productId : null;
                      
                      return (
                        <Chip
                          key={index}
                          label={name}
                          size="small"
                          color="primary"
                          variant="outlined"
                          onClick={() => handleAddSuggested(name, productId)}
                          icon={<LocalFireDepartmentIcon fontSize="small" />}
                          className="suggestion-chip"
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default CustomerIntelligence;
